// The `utm` cookie is the rolling list of utm_* params a visitor arrived
// with. BaseHandler reads it once at signup and copies it onto the user, where
// it feeds the analytics traits and the attribution survey. It is campaign
// measurement, so it is only written once Performance consent (OneTrust
// C0002) exists, the same gate GA waits for; in the US OneTrust grants every
// group on load. Logged-in visitors never need it: BaseHandler saves their
// params straight to the user record.
//
// The value is plain base64 JSON, the format BaseHandler.get_json_cookie
// decodes and set_json_cookie writes. Older marketing-site bundles wrote a
// percent-encoded value, which the server could not decode whenever the
// base64 needed padding; that form is still read here so nothing is lost.
import { runAfterAnalyticsConsent } from "./consent"
import { cookieValue, setStoryworthCookie } from "./cookies"

const UTM_COOKIE = "utm"
const UTM_COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60
// Both writers (this module and BaseHandler) keep only the most recent
// entries so the cookie cannot grow toward the header size limit.
const UTM_COOKIE_MAX_ENTRIES = 10
const UTM_VARIABLES = [
  "source",
  "medium",
  "term",
  "content",
  "campaign",
  "timestamp",
]

type UtmEntry = Record<string, string>

/**
 * Appends this page's utm_* params to the `utm` cookie once Performance
 * consent exists (immediately if it already does). Does nothing on a page
 * without a utm_source param, or for a logged-in visitor: BaseHandler saves
 * their params straight to the user, and a cookie would outlive their
 * session and attribute the next signup in the same browser to their
 * campaign.
 */
export function saveUtmParamsAfterAnalyticsConsent() {
  if (window.storyworthUserData || !utmParamsFromLocation()) return
  runAfterAnalyticsConsent(saveUtmParams)
}

function saveUtmParams() {
  const utm = utmParamsFromLocation()
  if (!utm) return

  const entries = [...storedUtmEntries(), utm].slice(-UTM_COOKIE_MAX_ENTRIES)
  setStoryworthCookie(
    UTM_COOKIE,
    base64OfUtf8(JSON.stringify(entries)),
    UTM_COOKIE_MAX_AGE_SECONDS,
  )
}

/** The entries currently in the `utm` cookie, or [] when absent or unreadable. */
export function storedUtmEntries(): UtmEntry[] {
  // Tornado quotes cookie values that contain "=", and the previous
  // marketing-site bundle percent-encoded them.
  const raw = cookieValue(UTM_COOKIE).replace(/^"|"$/g, "")
  if (!raw) return []
  try {
    const entries: unknown = JSON.parse(utf8OfBase64(decodeURIComponent(raw)))
    return Array.isArray(entries) ? entries : []
  } catch {
    return []
  }
}

// btoa/atob only handle Latin-1, so the JSON travels as UTF-8 bytes, which
// is also what the server's base64 + JSON decoding expects.
function base64OfUtf8(text: string) {
  const bytes = new TextEncoder().encode(text)
  let binary = ""
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte)
  })
  return btoa(binary)
}

function utf8OfBase64(base64: string) {
  const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0))
  return new TextDecoder().decode(bytes)
}

function utmParamsFromLocation(): UtmEntry | null {
  const searchParams = new URLSearchParams(window.location.search)
  if (!searchParams.get("utm_source")) return null

  const utm: UtmEntry = {}
  for (const name of UTM_VARIABLES) {
    const value = searchParams.get(`utm_${name}`)
    if (value) utm[name] = value
  }
  return utm
}
